import { Avatar } from '@/components/Avatar/Avatar';
import { Chip } from '@/components/Chip/Chip';
import { Modal } from '@/components/Modal/Modal';
import { useMember, useStaffProfileDetail } from '@/features/members';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import styles from './AgentDetailModal.module.css';

export type AgentDetailTarget =
  | { kind: 'member'; id: string }
  | { kind: 'staff_profile'; id: string };

interface AgentDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: AgentDetailTarget | null;
}

/**
 * Modal surfaced from a recommendation card "More info" button. Fetches the
 * full detail on-demand based on the target kind:
 *   - hired Member       → useMember (returns Member with positions/units)
 *   - unhired Staff      → useStaffProfileDetail (resolves to tenant overrides)
 *
 * Single component for both cases keeps the trigger UX symmetric across
 * recommendation sections.
 */
export function AgentDetailModal({ open, onOpenChange, target }: AgentDetailModalProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('Team member details')}
      description={t('Background, position, and how they work.')}
    >
      {target?.kind === 'member' ? <MemberBody id={target.id} /> : null}
      {target?.kind === 'staff_profile' ? <StaffProfileBody id={target.id} /> : null}
      {!target ? null : null}
    </Modal>
  );
}

function MemberBody({ id }: { id: string }) {
  const q = useMember(id);
  if (q.isLoading) return <div className={styles.loading}>{t('Loading…')}</div>;
  if (q.isError || !q.data) {
    return <div className={styles.error}>{t('Could not load this team member.')}</div>;
  }
  const m = q.data.member;
  const name = formatPersonName(m);
  return (
    <DetailLayout
      name={name}
      positions={m.positions}
      units={m.units}
      description={m.description}
      personality={m.personality}
      systemPrompt={m.systemPrompt}
    />
  );
}

function StaffProfileBody({ id }: { id: string }) {
  const q = useStaffProfileDetail(id);
  if (q.isLoading) return <div className={styles.loading}>{t('Loading…')}</div>;
  if (q.isError || !q.data) {
    return <div className={styles.error}>{t('Could not load this Staff Profile.')}</div>;
  }
  const p = q.data.item;
  const name = formatPersonName(p);
  return (
    <DetailLayout
      name={name}
      positions={p.defaultPositions}
      units={p.defaultUnits}
      description={p.description}
      personality={p.personality}
      systemPrompt={p.systemPrompt}
      addableHint
    />
  );
}

interface DetailLayoutProps {
  name: string;
  positions: { id: string; name: string }[];
  units: { id: string; name: string }[];
  description: string | null;
  personality: string | null;
  systemPrompt: string | null;
  addableHint?: boolean;
}

function DetailLayout({
  name,
  positions,
  units,
  description,
  personality,
  systemPrompt,
  addableHint,
}: DetailLayoutProps) {
  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <Avatar name={name} size="md" kind="ai" />
        <div>
          <h3 className={styles.name}>{name}</h3>
          {addableHint ? <p className={styles.addableHint}>{t('Not on your team yet.')}</p> : null}
        </div>
      </header>

      {description ? <p className={styles.description}>{description}</p> : null}

      {positions.length > 0 || units.length > 0 ? (
        <div className={styles.metaRow}>
          {positions.length > 0 ? (
            <MetaGroup label={positions.length === 1 ? t('Position') : t('Positions')}>
              {positions.map((p) => (
                <Chip key={p.id} variant="neutral" size="sm">
                  {p.name}
                </Chip>
              ))}
            </MetaGroup>
          ) : null}
          {units.length > 0 ? (
            <MetaGroup label={units.length === 1 ? t('Unit') : t('Units')}>
              {units.map((u) => (
                <Chip key={u.id} variant="neutral" size="sm">
                  {u.name}
                </Chip>
              ))}
            </MetaGroup>
          ) : null}
        </div>
      ) : null}

      {personality ? (
        <section className={styles.section}>
          <h4 className={styles.sectionHeading}>{t('How they work')}</h4>
          <p className={styles.prose}>{personality}</p>
        </section>
      ) : null}

      {systemPrompt ? (
        <section className={styles.section}>
          <h4 className={styles.sectionHeading}>{t('Detailed instructions')}</h4>
          <pre className={styles.systemPrompt}>{systemPrompt}</pre>
        </section>
      ) : null}
    </div>
  );
}

function MetaGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.metaGroup}>
      <span className={styles.metaLabel}>{label}</span>
      <div className={styles.chips}>{children}</div>
    </div>
  );
}
